import { Button, ButtonGroup } from "@datapay-by-datacom/design-system";
import { useNavigate } from "react-router";
import {
  InvocationStatusEnum,
  MigrationRunStatus,
} from "../../generated/core-api";
import {
  buildCommittedLookupTargets,
  useBulkCreateCommitCompanyLookups,
  useCompanyProvisionAgentId,
  useCompanyProvisionWizard,
  useMyPayEnvironmentLabel,
} from "../hooks";
import type {
  BulkCreateCommittedRowLookup,
  CompanyShellWorkloadsManagerReturn,
} from "../hooks";
import { useBulkCreateFlow } from "../bulk-create-redesign/bulkCreateFlow";
import {
  getCommittedRowResults,
  getNonCommittedRowResults,
  type CompanyShellRowResult,
} from "../types/companyShellCommit";
import { INITIAL_WIZARD_STATE } from "../components";
import s from "./BulkCreateCompanyResultsScreen.module.css";

const MISSING_COMPANY_CODE_LABEL = "—";

function displayCompanyName(row: BulkCreateCommittedRowLookup): string {
  if (row.status === "resolved" && row.lookup?.companyName) {
    return row.lookup.companyName;
  }
  return row.fallbackName || row.companyCode;
}

function formatRowErrors(row: CompanyShellRowResult): string {
  if (!row.errors?.length) {
    if (row.status === "failed_requires_intervention") {
      return "This company could not be created and may require manual follow-up.";
    }
    return "This row was not created.";
  }
  return row.errors.map((e) => e.message).join("; ");
}

/**
 * Results screen — step 3 of the bulk company-creation wizard.
 *
 * Step 16 (LADR-039 / #440): dispatches on `shellManager` from wizard
 * context, same as `BulkCreateCompanyReviewScreen` — `null` renders Track
 * 1's behaviour below (resumes polling the commit invocation Review started
 * via `commitWorkflowId` in wizard state, so this works even after a fresh
 * navigation/reload, until it reaches a terminal status, then lists created
 * companies and surfaces failures without green success copy on partial
 * outcomes — #433), a `shellManager` instance renders Track 2's (reads the
 * migration run's `runId`/`runStatus` straight off the Provider-owned
 * manager instance — see `BulkCreateCompanyWizardProvider`'s module doc for
 * why Track 2 has no cross-reload resume path today, unlike Track 1's
 * `commitWorkflowId`).
 */
export function BulkCreateCompanyResultsScreen() {
  const { shellManager } = useCompanyProvisionWizard();
  return shellManager ? (
    <BulkCreateCompanyResultsScreenWorkloads manager={shellManager} />
  ) : (
    <BulkCreateCompanyResultsScreenTrackOne />
  );
}

/** Track 1 (flag off / default): resumable polling via `commitWorkflowId` + `useBulkCreateCommitCompanyLookups`. */
function BulkCreateCompanyResultsScreenTrackOne() {
  const navigate = useNavigate();
  const flow = useBulkCreateFlow();
  const { state, updateState } = useCompanyProvisionWizard();
  const {
    agentId,
    isConfigured,
    isLoading: isAgentLoading,
  } = useCompanyProvisionAgentId();
  const { label: environmentLabel } = useMyPayEnvironmentLabel(
    state.mypayEnvironment
  );

  const {
    statusQuery,
    invocationStatus: status,
    commitResult,
    outcomeKind,
    committedRowLookups,
    isResolvingLookups,
  } = useBulkCreateCommitCompanyLookups({
    agentId,
    workflowId: state.commitWorkflowId,
    mypayEnvironment: state.mypayEnvironment,
    wizardRows: state.rows,
  });

  const handleStartOver = () => {
    updateState({ ...INITIAL_WIZARD_STATE });
    navigate(flow.uploadPath);
  };

  if (!state.commitWorkflowId) {
    return (
      <div className={s.container}>
        <header className={s.header}>
          <h1 className={s.title}>Results</h1>
          <p className={s.subtitle}>
            No commit in progress. Please start from Upload.
          </p>
        </header>
        <ButtonGroup align="right">
          <Button variant="primary" onPress={handleStartOver}>
            Back to upload
          </Button>
        </ButtonGroup>
      </div>
    );
  }

  const isPollingActive =
    (isAgentLoading || isConfigured) && !statusQuery.isError;
  const cannotTrackStatus =
    !isAgentLoading && (!isConfigured || statusQuery.isError);

  const showCommitOutcome =
    status === InvocationStatusEnum.COMPLETED &&
    commitResult !== null &&
    commitResult.row_count > 0;

  // The commit payload isn't formalized in OpenAPI, so a terminal COMPLETED
  // invocation can still fail to parse (null or unrecognized shape). An empty
  // `row_count` roll-up is treated the same way — not a success banner.
  const commitResultUnreadable =
    status === InvocationStatusEnum.COMPLETED &&
    (commitResult === null ||
      (commitResult !== null && commitResult.row_count === 0));

  const failedRows = commitResult ? getNonCommittedRowResults(commitResult) : [];
  const succeededRowCount = commitResult
    ? getCommittedRowResults(commitResult).length
    : 0;
  const followUpRowCount = failedRows.length;

  const isCommitInProgress =
    isPollingActive &&
    (status === null || status === InvocationStatusEnum.RUNNING);

  const committedLookupTargetCount =
    showCommitOutcome && commitResult
      ? buildCommittedLookupTargets(commitResult, state.rows).length
      : 0;

  const isConfirmingCreatedCompanies =
    showCommitOutcome &&
    committedLookupTargetCount > 0 &&
    (isResolvingLookups ||
      committedRowLookups.some((row) => row.status === "loading") ||
      (state.mypayEnvironment != null && committedRowLookups.length === 0));

  const isStartOverDisabled = isCommitInProgress || isConfirmingCreatedCompanies;

  return (
    <div className={s.container}>
      <header className={s.header}>
        <h1 className={s.title}>Results</h1>
      </header>

      {isPollingActive &&
        (status === null || status === InvocationStatusEnum.RUNNING) && (
          <p className={s.status} role="status">
            Committing… this can take a moment.
          </p>
        )}

      {cannotTrackStatus && (
        <p className={s.warning} role="alert">
          {statusQuery.isError
            ? "We couldn't fetch the commit status. The companies may still have been created — contact support before trying the upload again."
            : "Commit status isn't available in this environment yet, so we can't confirm whether the companies were created. Contact support before trying the upload again."}
        </p>
      )}

      {commitResultUnreadable && (
        <p className={s.warning} role="alert">
          We couldn&apos;t read the commit result. The companies may still
          have been created. Contact support before trying the upload again.
        </p>
      )}

      {showCommitOutcome && !state.mypayEnvironment && (
        <p className={s.warning} role="alert">
          The selected Datapay environment is missing, so we can&apos;t confirm
          companies against Datapay. Names and codes below are from your upload.
        </p>
      )}

      {showCommitOutcome && outcomeKind === "full_success" && (
        <p className={s.okBanner} role="status">
          {succeededRowCount === 1
            ? "Company created successfully."
            : `All ${succeededRowCount} companies created successfully.`}
        </p>
      )}

      {showCommitOutcome && outcomeKind === "partial" && (
        <p className={s.warning} role="alert">
          Create results: {succeededRowCount} of {commitResult!.row_count}{" "}
          succeeded. {followUpRowCount}{" "}
          {followUpRowCount === 1 ? "needs" : "need"} follow-up — see details
          below.
        </p>
      )}

      {showCommitOutcome && outcomeKind === "all_failed" && (
        <p className={s.warning} role="alert">
          The commit did not create any companies. See the issues below for each
          row.
        </p>
      )}

      {(status === InvocationStatusEnum.FAILED ||
        status === InvocationStatusEnum.TERMINATED) && (
        <p className={s.warning} role="alert">
          The commit stopped before its result could be confirmed. Some
          companies may have been created.
          {statusQuery.data?.error ? ` ${statusQuery.data.error}` : ""}
        </p>
      )}

      {showCommitOutcome && committedRowLookups.length > 0 && (
        <section aria-labelledby="bulk-create-results-created-heading">
          <h2 id="bulk-create-results-created-heading" className={s.sectionTitle}>
            Created companies
          </h2>
          <table className={s.resultsTable}>
            <thead>
              <tr>
                <th scope="col">Company name</th>
                <th scope="col">Company code</th>
                <th scope="col">Datapay environment</th>
              </tr>
            </thead>
            <tbody>
              {committedRowLookups.map((row) => (
                <tr key={row.rowIndex}>
                  <td>
                    {displayCompanyName(row)}
                    {row.status === "loading" && (
                      <span className={s.muted}>Confirming…</span>
                    )}
                    {row.status === "error" && row.errorMessage ? (
                      <span className={s.failedReason}>
                        {row.errorMessage}
                      </span>
                    ) : null}
                  </td>
                  <td>
                    {row.companyCode.trim()
                      ? row.companyCode
                      : MISSING_COMPANY_CODE_LABEL}
                  </td>
                  <td>{environmentLabel ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {isResolvingLookups && (
            <p className={s.muted} role="status">
              {committedLookupTargetCount === 1
                ? "Confirming company in Datapay…"
                : "Confirming companies in Datapay…"}
            </p>
          )}
        </section>
      )}

      {showCommitOutcome && failedRows.length > 0 && (
        <section aria-labelledby="bulk-create-results-failed-heading">
          <h2 id="bulk-create-results-failed-heading" className={s.sectionTitle}>
            Rows that need follow-up
          </h2>
          <ul className={s.failedList}>
            {failedRows.map((row) => {
              const wizardRow = state.rows[row.row_index];
              const code =
                row.company_code?.trim() ||
                wizardRow?.company_code ||
                `Row ${row.row_index + 1}`;
              return (
                <li key={row.row_index}>
                  <strong>{code}</strong>
                  <span className={s.failedReason}>{formatRowErrors(row)}</span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <ButtonGroup align="right">
        <Button
          variant="primary"
          onPress={handleStartOver}
          isDisabled={isStartOverDisabled}
        >
          Start another bulk upload
        </Button>
      </ButtonGroup>
    </div>
  );
}

/**
 * Track 2 (flag on): reads the migration run's status straight from the
 * Provider-owned `shellManager` instance — no separate poll hook needed,
 * `useCompanyShellWorkloadsManager` composes `useOnboardingMigrationRun`
 * (which already polls) internally.
 *
 * `RunView` (the migration run's API shape) has no field analogous to Track
 * 1's defensive `FAILED_REQUIRES_INTERVENTION` check — `MigrationRunStatus`
 * is a plain `queued | running | succeeded | failed` — so there is no
 * intervention banner, confirmed-companies lookup table, or per-row
 * follow-up list to build here; a `failed` run is unambiguous and the only
 * identifier available is the run id itself.
 */
function BulkCreateCompanyResultsScreenWorkloads({
  manager,
}: {
  manager: CompanyShellWorkloadsManagerReturn;
}) {
  const navigate = useNavigate();
  const { updateState } = useCompanyProvisionWizard();

  const handleStartOver = () => {
    // Defensively reset both the Track 2 manager and wizard context: the
    // Provider's seeding effect already no-ops once `state.rows` is empty
    // (see `BulkCreateCompanyWizardProvider`), but clearing the manager here
    // means a stale run's status/reference can't flash if this screen is
    // reached again before a new upload seeds fresh data.
    manager.clear();
    updateState({ ...INITIAL_WIZARD_STATE });
    navigate("/onboarding/bulk-create-company");
  };

  if (!manager.runId) {
    return (
      <div className={s.container}>
        <header className={s.header}>
          <h1 className={s.title}>Results</h1>
          <p className={s.subtitle}>
            No commit in progress. Please start from Upload.
          </p>
        </header>
        <ButtonGroup align="right">
          <Button variant="primary" onPress={handleStartOver}>
            Back to upload
          </Button>
        </ButtonGroup>
      </div>
    );
  }

  const status = manager.runStatus;
  const isPollingActive =
    status === null ||
    status === MigrationRunStatus.queued ||
    status === MigrationRunStatus.running;

  return (
    <div className={s.container}>
      <header className={s.header}>
        <h1 className={s.title}>Results</h1>
      </header>

      {isPollingActive && (
        <p className={s.status} role="status">
          Committing… this can take a moment.
        </p>
      )}

      {status === MigrationRunStatus.succeeded && (
        <p className={s.okBanner} role="status">
          Companies created successfully.
        </p>
      )}

      {status === MigrationRunStatus.failed && (
        <p className={s.warning} role="alert">
          The commit failed — nothing was created.
          {manager.managerError ? ` ${manager.managerError}` : ""}
        </p>
      )}

      <p className={s.muted}>
        Run reference: <code>{manager.runId}</code>
      </p>

      <ButtonGroup align="right">
        <Button variant="primary" onPress={handleStartOver}>
          Start another bulk upload
        </Button>
      </ButtonGroup>
    </div>
  );
}
